import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ThemeContext = createContext(null);

const DEFAULT_THEME = {
  midnight: "#14181F",
  midnight_2: "#1A1F2A",
  midnight_3: "#222836",
  coral: "#FF6B35",
  beige: "#F5F5F0",
  gold: "#D4AF37",
  hero_overlay_left: 1,
  hero_overlay_mid: 0.85,
  hero_overlay_right: 0.3,
  hero_overlay_bottom: 1,
  hero_overlay_center: 0.3,
  font_heading: '"Cabinet Grotesk", "Manrope", ui-sans-serif, system-ui, sans-serif',
  font_body: '"Manrope", ui-sans-serif, system-ui, sans-serif',
  font_menu: '"Cabinet Grotesk", "Manrope", ui-sans-serif, system-ui, sans-serif',
  font_logo: '"Cabinet Grotesk", "Manrope", ui-sans-serif, system-ui, sans-serif',
  font_mono: '"JetBrains Mono", ui-monospace, monospace',
};

const normalizeOpacity = (value, fallback) => {
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return Math.max(0, Math.min(1, n));
};

const hexToRgbTriplet = (hex) => {
  if (!hex) return null;
  const m = hex.replace("#", "").trim();
  const v = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  if (v.length !== 6) return null;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
};

const applyTheme = (theme) => {
  const root = document.documentElement;
  const map = {
    midnight: "--c-midnight",
    midnight_2: "--c-midnight-2",
    midnight_3: "--c-midnight-3",
    coral: "--c-coral",
    beige: "--c-beige",
    gold: "--c-gold",
  };
  Object.entries(map).forEach(([k, css]) => {
    const triplet = hexToRgbTriplet(theme[k]);
    if (triplet) root.style.setProperty(css, triplet);
  });

  const fontMap = {
    font_heading: "--font-heading",
    font_body: "--font-body",
    font_menu: "--font-menu",
    font_logo: "--font-logo",
    font_mono: "--font-mono",
  };
  Object.entries(fontMap).forEach(([k, css]) => {
    if (theme[k]) root.style.setProperty(css, theme[k]);
  });

  const overlayMap = {
    hero_overlay_left: ["--hero-ol-left", DEFAULT_THEME.hero_overlay_left],
    hero_overlay_mid: ["--hero-ol-mid", DEFAULT_THEME.hero_overlay_mid],
    hero_overlay_right: ["--hero-ol-right", DEFAULT_THEME.hero_overlay_right],
    hero_overlay_bottom: ["--hero-ol-bottom", DEFAULT_THEME.hero_overlay_bottom],
    hero_overlay_center: ["--hero-ol-center", DEFAULT_THEME.hero_overlay_center],
  };
  Object.entries(overlayMap).forEach(([k, [css, fallback]]) => {
    root.style.setProperty(css, String(normalizeOpacity(theme[k], fallback)));
  });
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(DEFAULT_THEME);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API_BASE}/theme`);
      const next = { ...DEFAULT_THEME, ...res.data };
      setTheme(next);
      applyTheme(next);
    } catch {
      applyTheme(DEFAULT_THEME);
    }
  };

  useEffect(() => {
    applyTheme(DEFAULT_THEME);
    refresh();
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, refresh, applyTheme, DEFAULT_THEME }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
